import React from 'react';
import classnames from 'classnames';
import styles from './Row.module.scss';

/**
 * ##Import
 * `import Row from "components/atoms/Grid/Row/Row";`
 */

type VerticalAlignment = 'top' | 'middle' | 'bottom' | 'stretch';
type HorizontalArrangement =
  | 'start'
  | 'end'
  | 'center'
  | 'space-around'
  | 'space-between'
  | 'space-evenly';
type GutterValue = 0 | 4 | 8 | 16 | 24 | 32;
type ResponsiveGutter = [GutterValue, GutterValue];
type Layout = 'horizontal' | 'vertical';

interface ResponsiveAlignment {
  xs?: VerticalAlignment;
  sm?: VerticalAlignment;
  md?: VerticalAlignment;
  lg?: VerticalAlignment;
  xl?: VerticalAlignment;
  xxl?: VerticalAlignment;
}

interface ResponsiveJustify {
  xs?: HorizontalArrangement;
  sm?: HorizontalArrangement;
  md?: HorizontalArrangement;
  lg?: HorizontalArrangement;
  xl?: HorizontalArrangement;
  xxl?: HorizontalArrangement;
}

interface ResponsiveGutterObject {
  xs?: ResponsiveGutter;
  sm?: ResponsiveGutter;
  md?: ResponsiveGutter;
  lg?: ResponsiveGutter;
  xl?: ResponsiveGutter;
  xxl?: ResponsiveGutter;
}

export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Vertical alignment
   */
  align?: VerticalAlignment | ResponsiveAlignment;
  /**
   * Horizontal arrangement
   */
  justify?: HorizontalArrangement | ResponsiveJustify;
  /**
   * Auto wrap line
   */
  wrap?: boolean;
  /**
   * Spacing between grids, could be an array [horizontal, vertical] or an object like { xs: [horizontal, vertical], sm: [horizontal, vertical], md: [horizontal, vertical]}.
   */
  gutter?: ResponsiveGutter | ResponsiveGutterObject;
  children?: React.ReactNode;
  /**
   * rowReverse can be used to reverse the contents of the row
   */
  rowReverse?: boolean;
  /**
   * Layout direction
   */
  layout?: Layout;
  className?: string;
}

const Row: React.FC<RowProps> = ({
  align = 'top',
  gutter = { xs: [8, 8], sm: [8, 8], md: [16, 16], lg: [16, 16], xl: [24, 24], xxl: [24, 24] },
  justify = 'start',
  wrap = true,
  children,
  rowReverse = false,
  layout = 'horizontal',
  className,
  ...props
}) => {
  let classList = [styles.row] as string[];

  if (gutter) {
    if (Array.isArray(gutter)) {
      classList = [...classList, styles[`rowGap${gutter[0]}`], styles[`colGap${gutter[1]}`]];
    } else {
      for (const size in gutter) {
        const sizeKey = size as keyof ResponsiveGutterObject;
        const gutterValue = gutter[sizeKey];
        if (gutterValue) {
          classList = [
            ...classList,
            styles[`${size}RowGap${gutterValue[0]}`],
            styles[`${size}ColGap${gutterValue[1]}`],
          ];
        }
      }
    }
  }

  const normalizePropertyName = (propertyName: string): string => {
    const names = propertyName.split('-');
    if (names.length === 1) {
      return names[0];
    }
    return names.reduce((acc, word, index) => {
      const capitalizedWord = index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
      return acc + capitalizedWord;
    }, '');
  };

  const updatePosition = (property: string | Record<string, string>) => {
    if (typeof property === 'string') {
      classList = [...classList, styles[normalizePropertyName(property)]];
    } else {
      for (const size in property) {
        classList = [...classList, styles[normalizePropertyName(property[size])]];
      }
    }
  };

  align && updatePosition(align as string | Record<string, string>);
  justify && updatePosition(justify as string | Record<string, string>);

  return (
    <div
      className={classnames(
        ...classList,
        {
          [styles.wrap]: wrap,
          [styles.noWrap]: !wrap,
          [styles.rowReverse]: rowReverse,
          [styles[layout]]: layout,
        },
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

Row.displayName = 'Row';

export default Row;
